<template>
  <div
    v-if="applicationReady && componentReady"
    class="questionnaire"
  >
    <div class="questionnaire-top">
      <b-row>
        <b-col
          v-if="!formSubmitted"
          cols="12"
        >
          <b-row>
            <b-col
              v-if="currentPage > 1"
              cols="12"
              :class="questionClasses"
            >
              <h1>
                {{ currentQuestion.question }}
              </h1>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              v-if="currentPage > 1 && currentQuestion.subQuestion"
              cols="12"
              class="sub-question"
            >
              <h5>
                {{ currentQuestion.subQuestion }}
              </h5>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-row class="question-errors">
                <b-col
                  v-if="currentQuestion.section === 'temperature' && $v.formAnswers.temperature.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.temperature.required"
                    class="invalid-feedback"
                  >
                    <h3>Temperature is required</h3>
                  </div>

                  <div
                    v-if="!$v.formAnswers.temperature.decimal"
                    class="invalid-feedback"
                  >
                    <h3>Temperature must be a decimal</h3>
                  </div>

                  <div
                    v-if="!$v.formAnswers.temperature.between"
                    class="invalid-feedback"
                  >
                    <h3>Enter a temperature between {{ lowTemp }}&deg; and {{ highTemp }}&deg; F to continue</h3>
                  </div>
                </b-col>

                <b-col
                  v-if="currentQuestion.section === 'diagnosedWithCovid' && $v.formAnswers.diagnosedWithCovid.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.diagnosedWithCovid.required || !$v.formAnswers.diagnosedWithCovid.mustBeTrue"
                    class="invalid-feedback"
                  >
                    <h3>Choose No or Yes to continue</h3>
                  </div>
                </b-col>

                <b-col
                  v-if="currentQuestion.section === 'experiencedAnyOfTheseSymptoms' && $v.formAnswers.experiencedAnyOfTheseSymptoms.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.experiencedAnyOfTheseSymptoms.required || !$v.formAnswers.experiencedAnyOfTheseSymptoms.mustBeTrue"
                    class="invalid-feedback"
                  >
                    <h3>Choose an option to continue</h3>
                  </div>
                </b-col>

                <b-col
                  v-if="currentQuestion.section === 'experiencedAnyOfTheseAdditionalSymptoms' && $v.formAnswers.experiencedAnyOfTheseAdditionalSymptoms.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.experiencedAnyOfTheseAdditionalSymptoms.required || !$v.formAnswers.experiencedAnyOfTheseAdditionalSymptoms.mustBeTrue"
                    class="invalid-feedback"
                  >
                    <h3>Choose an option to continue</h3>
                  </div>
                </b-col>

                <b-col
                  v-if="currentQuestion.section === 'visitedStates' && $v.formAnswers.visitedStates.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.visitedStates.required || !$v.formAnswers.visitedStates.mustBeTrue"
                    class="invalid-feedback"
                  >
                    <h3>Choose No or Yes to continue</h3>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <smooth-reflow>
                <b-form @submit.prevent>
                  <b-form-row
                    v-if="currentPage === 1"
                    class="question-answer"
                  >
                    <b-col cols="12">
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                          offset-md="3"
                          class="text-center pb-4"
                        >
                          <h1>
                            Enter date and time of entry
                          </h1>
                        </b-col>
                      </b-row>

                      <b-row>
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              v-if="$v.formAnswers.enterDateOfEntry.$error || $v.formAnswers.enterTimeOfEntry.$error"
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                class="invalid-feedback"
                              >
                                <h3>Date and time of entry is required</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                          offset-md="3"
                        >
                          <b-form-datepicker
                            id="example-datepicker"
                            v-model="formAnswers.enterDateOfEntry"
                            class="mb-2"
                            :max="max"
                            :date-format-options="dateFormatOptions"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                        >
                          <b-form-timepicker
                            v-model="formAnswers.enterTimeOfEntry"
                            locale="en"
                          />
                        </b-col>
                      </b-form-row>

                      <b-row>
                        <b-col
                          cols="12"
                          :class="questionClasses"
                        >
                          <h1>
                            Take and record temperatures
                          </h1>
                        </b-col>
                      </b-row>

                      <b-row>
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              v-if="$v.formAnswers.temperature.$error"
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.temperature.required"
                                class="invalid-feedback"
                              >
                                <h3>Temperature is required</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.temperature.decimal"
                                class="invalid-feedback"
                              >
                                <h3>Temperature must be a decimal</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.temperature.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a temperature between {{ lowTemp }}&deg; and {{ highTemp }}&deg; F to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="6"
                          class="pb-3"
                          offset-md="3"
                        >
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.temperature"
                            step=".1"
                            placeholder="98.6"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = '98.6'"
                            @keyup.enter="checkQuestion"
                          />
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-else-if="currentQuestion.section === 'diagnosedWithCovid'"
                    class="question-answer"
                  >
                    <b-col
                      cols="12"
                      md="6"
                      class="pb-3"
                      offset-md="3"
                    >
                      <b-form-row class="question-checkboxes">
                        <b-col
                          v-for="value in currentQuestion.values"
                          :key="value.id"
                          cols="12"
                          md="6"
                          class="pb-3 px-4"
                        >
                          <b-form-checkbox
                            class="question-checkbox"
                            size="lg"
                            v-model="formAnswers.diagnosedWithCovid[value.id]"
                            :disabled="checkboxDisabledState[currentQuestion.id][value.id]"
                            text-field="answer_text"
                            value-field="answer_value"
                          >
                            {{ value.answer_text }}
                          </b-form-checkbox>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-else-if="currentQuestion.section === 'experiencedAnyOfTheseSymptoms'"
                    class="question-answer"
                  >
                    <b-col
                      cols="12"
                      class="pb-3 px-4"
                    >
                      <b-form-row class="question-checkboxes">
                        <b-col
                          v-for="value in currentQuestion.values"
                          :key="value.id"
                          cols="12"
                          md="6"
                          class="pb-3 px-4"
                        >
                          <b-form-checkbox
                            :class="questionCheckboxClasses(value)"
                            size="lg"
                            v-model="formAnswers.experiencedAnyOfTheseSymptoms[value.id]"
                            :disabled="checkboxDisabledState[currentQuestion.id][value.id]"
                            text-field="answer_text"
                            value-field="answer_value"
                          >
                            {{ value.answer_text }}
                          </b-form-checkbox>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-else-if="currentQuestion.section === 'experiencedAnyOfTheseAdditionalSymptoms'"
                    class="question-answer"
                  >
                    <b-col
                      cols="12"
                      class="pb-3 px-4"
                    >
                      <b-form-row class="question-checkboxes">
                        <b-col
                          v-for="value in currentQuestion.values"
                          :key="value.id"
                          cols="12"
                          md="6"
                          class="pb-3 px-4"
                        >
                          <b-form-checkbox
                            :class="questionCheckboxClasses(value)"
                            size="lg"
                            v-model="formAnswers.experiencedAnyOfTheseAdditionalSymptoms[value.id]"
                            :disabled="checkboxDisabledState[currentQuestion.id][value.id]"
                            text-field="answer_text"
                            value-field="answer_value"
                          >
                            {{ value.answer_text }}
                          </b-form-checkbox>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-else-if="currentQuestion.section === 'visitedStates'"
                    class="question-answer"
                  >
                    <b-col
                      cols="12"
                      md="6"
                      class="pb-3"
                      offset-md="3"
                    >
                      <b-form-row>
                        <b-col
                          cols="12"
                          class="pb-3 px-4 text-center"
                        >
                          <h3>
                            {{ hotspots }}
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-form-row class="question-checkboxes">
                        <b-col
                          v-for="value in currentQuestion.values"
                          :key="value.id"
                          cols="12"
                          md="6"
                          class="pb-3 px-4"
                        >
                          <b-form-checkbox
                            class="question-checkbox"
                            size="lg"
                            v-model="formAnswers.visitedStates[value.id]"
                            :disabled="checkboxDisabledState[currentQuestion.id][value.id]"
                            text-field="answer_text"
                            value-field="answer_value"
                          >
                            {{ value.answer_text }}
                          </b-form-checkbox>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>
                </b-form>
              </smooth-reflow>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          v-if="formSubmitted"
          cols="12"
          class="screening-complete"
        >
          <b-row>
            <b-col
              cols="12"
              class="screening-complete-title"
            >
              <span
                v-if="!failedScreening"
                class="success-message exit"
              >
                <h1>
                  Screening Complete!
                </h1>
              </span>

              <b-row
                v-else
                class="failure-message"
              >
                <b-col
                  cols="12"
                  md="3"
                  class="screening-complete-icon d-flex flex-column justify-content-center align-items-middle"
                >
                  <div class="row justify-content-center justify-content-md-start">
                    <img
                      class="stop-sign"
                      src="../assets/stop-sign.png"
                    >
                  </div>
                </b-col>
                <b-col
                  cols="12"
                  md="9"
                  class="screening-complete-message d-flex flex-column justify-content-center"
                >
                  <h1>
                    {{ failureStopDescription }}
                  </h1>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row class="mt-4">
            <b-col
              cols="12"
              md="3"
              class="screening-complete-icon d-flex flex-column justify-content-center"
            >
              <img
                class="speak-icon d-none d-md-block"
                src="../assets/speak-icon.png"
              >
            </b-col>
            <b-col
              cols="12"
              md="9"
              class="screening-complete-body"
            >
              <div class="message">
                <span v-if="!failedScreening">
                  Please be sure to wear a mask at all times and wash hands or use hand sanitizer when needed for the safety of everyone in the Branch.
                </span>
                <span v-else>
                  <span v-if="isResident">
                    Please provide the resident a mask and escort them to their apartment. Request that they isolate in their apartment and notify the RN Coordinator.
                  </span>

                  <span v-else-if="isBfm">
                    Please put on a clean mask and exit the Branch. Call your primary care physician, socially distance and isolate at home. Monitor your symptoms and notify the Branch Director.
                  </span>

                  <span v-else>
                    Please put on a clean mask and exit the Branch. Call your primary care physician, socially distance and isolate at home.
                  </span>
                </span>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <div class="questionnaire-bottom">
      <b-row>
        <b-col cols="12">
          <b-form>
            <b-form-row class="justify-content-center no-gutters">
              <b-col
                v-if="!formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-2"
              >
                <b-button
                  @click="previousPage"
                  :disabled="canGoBack"
                  class="btn-green btn-nav"
                >
                  <b-icon-caret-left-fill />
                </b-button>
              </b-col>

              <b-col
                v-if="!canSubmitForm && !this.failedScreening"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-3"
              >
                <b-button
                  @click="checkQuestion"
                  class="btn-green btn-nav"
                >
                  <b-icon-caret-right-fill />
                </b-button>
              </b-col>

              <b-col
                v-if="canSubmitForm && !formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-3"
              >
                <b-button
                  @click="checkQuestion"
                  class="btn-green btn-nav"
                >
                  Submit
                </b-button>
              </b-col>

              <b-col
                v-if="formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation"
              >
                <b-button
                  @click="goHome"
                  class="btn-green btn-nav"
                >
                  <img
                    src="../assets/home-icon.png"
                    class="screening-icon"
                  >
                </b-button>
              </b-col>

              <b-col
                v-if="!formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-1"
              >
                <b-button
                  @click="goHome"
                  class="btn-green btn-nav order-md-1"
                >
                  <img
                    src="../assets/home-icon.png"
                  >
                </b-button>
              </b-col>

              <b-col
                v-if="formSubmitted && !isSelfMode"
                cols="6"
                sm="4"
                class="pb-3 question-navigation"
              >
                <b-button
                  @click="logout"
                  class="btn-green btn-nav"
                >
                  <img
                    src="../assets/log-out-icon-white.png"
                    class="icon-with-text"
                  >
                  Log out
                </b-button>
              </b-col>
            </b-form-row>
          </b-form>
        </b-col>
      </b-row>
    </div>

    <b-modal
      v-model="showFailedScreeningModal"
      id="failed-screening"
      modal-class="failed-screening"
      centered
      no-close-on-esc
      hide-header
      hide-footer
    >
      <div class="failed-screening-icon">
        <img
          src="../assets/warning-icon.png"
          class="icon"
        >
      </div>

      <p class="my-4 text-center">
        Confirm {{ failureDescription }}
      </p>

      <b-row>
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="submitFormFailure"
          >
            Confirm &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click="showFailedScreeningModal = !showFailedScreeningModal"
          >
            &laquo; Go back
          </b-button>
        </b-col>
      </b-row>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import { LOW_TEMP, HIGH_TEMP, STOP_TEMP } from '../store/modules/form-types'
import { decimal, required, between } from 'vuelidate/lib/validators'
import { mustBeTrue } from '../plugins/validators'
import { mapGetters } from 'vuex'
import mixin from '../mixins/forms.js'

export default {
  middleware: 'auth',
  name: 'EntryScreening',
  mixins: [mixin],
  components: {
    'smooth-reflow': () => import(/* webpackPrefetch: true */ /* webpackChunkName: "smooth-reflow" */ '../components/SmoothReflow')
  },
  data () {
    return {
      formAnswers: {
        enterDateOfEntry: '',
        enterTimeOfEntry: '',
        temperature: '',
        diagnosedWithCovid: [],
        experiencedAnyOfTheseSymptoms: [],
        experiencedAnyOfTheseAdditionalSymptoms: [],
        visitedStates: []
      }
    }
  },
  validations () {
    if (this.hasHotspots) {
      return {
        formAnswers: {
          enterDateOfEntry: {
            required
          },
          enterTimeOfEntry: {
            required
          },
          temperature: {
            required,
            decimal,
            between: between(LOW_TEMP, HIGH_TEMP),
          },
          diagnosedWithCovid: {
            required,
            mustBeTrue
          },
          experiencedAnyOfTheseSymptoms: {
            required,
            mustBeTrue
          },
          experiencedAnyOfTheseAdditionalSymptoms: {
            required,
            mustBeTrue
          },
          visitedStates: {
            required,
            mustBeTrue
          }
        }
      }
    } else {
      return {
        formAnswers: {
          enterDateOfEntry: {
            required
          },
          enterTimeOfEntry: {
            required
          },
          temperature: {
            required,
            decimal,
            between: between(90, 106)
          },
          diagnosedWithCovid: {
            required,
            mustBeTrue
          },
          experiencedAnyOfTheseSymptoms: {
            required,
            mustBeTrue
          },
          experiencedAnyOfTheseAdditionalSymptoms: {
            required,
            mustBeTrue
          }
        }
      }
    }
  },
  mounted () {
    this.init()
  },
  computed: {
    ...mapGetters({
      user: 'authentication/user',
      hotspots: 'forms/hotspots',
      hasHotspots: 'forms/hasHotspots'
    }),
    componentReady () {
      if (this.loggedIn) {
        return this.currentQuestionnaire !== undefined &&
          this.questions !== undefined &&
          this.questionAnswersAndStatesSetup &&
          (this.currentQuestion !== undefined || this.formSubmitted)
      }
      return false
    },
    checkboxDisabledLength () {
      return this.checkboxDisabledState.length
    },
    diagnosedWithCovid () {
      return this.formAnswers.diagnosedWithCovid
    },
    experiencedAnyOfTheseSymptoms () {
      return this.formAnswers.experiencedAnyOfTheseSymptoms
    },
    experiencedAnyOfTheseAdditionalSymptoms () {
      return this.formAnswers.experiencedAnyOfTheseAdditionalSymptoms
    },
    visitedStates () {
      return this.formAnswers.visitedStates
    },
    questionDate () {
      return this.findQuestionBySection('enterDateOfEntry')
    },
    questionTime () {
      return this.findQuestionBySection('enterTimeOfEntry')
    },
    questionTemperature () {
      return this.findQuestionBySection('temperature')
    }
  },
  watch: {
    diagnosedWithCovid: {
      handler: function (newAnswers, oldAnswers) {
        if (newAnswers !== undefined) {
          const values = this.currentQuestion.values
          const noValue = values.find(value => value.answer_text.toLowerCase() === 'no')
          const yesValue = values.find(value => value.answer_text.toLowerCase() === 'yes')
          let noDisabled = false
          let yesDisabled = false
          newAnswers.forEach((isChecked, key) => {
            const value = values.find(value => value.id === key)
            if (this.isNoOption(value)) {
              yesDisabled = isChecked
            } else {
              noDisabled = isChecked
            }
            return true
          })

          this.checkboxDisabledState[this.currentQuestionId][noValue.id] = noDisabled
          this.checkboxDisabledState[this.currentQuestionId][yesValue.id] = yesDisabled
        }
      },
      deep: true
    },
    experiencedAnyOfTheseSymptoms: {
      handler: 'setDisabledStateForMoreThanTwoCheckboxes',
      deep: true
    },
    experiencedAnyOfTheseAdditionalSymptoms: {
      handler: 'setDisabledStateForMoreThanTwoCheckboxes',
      deep: true
    },
    visitedStates: {
      handler: function (newAnswers, oldAnswers) {
        if (newAnswers !== undefined) {
          const values = this.currentQuestion.values
          const noValue = values.find(value => value.answer_text.toLowerCase() === 'no')
          const yesValue = values.find(value => value.answer_text.toLowerCase() === 'yes')
          let noDisabled = false
          let yesDisabled = false
          newAnswers.forEach((isChecked, key) => {
            const value = values.find(value => value.id === key)
            if (this.isNoOption(value)) {
              yesDisabled = isChecked
            } else {
              noDisabled = isChecked
            }
            return true
          })

          this.checkboxDisabledState[this.currentQuestionId][noValue.id] = noDisabled
          this.checkboxDisabledState[this.currentQuestionId][yesValue.id] = yesDisabled
        }
      },
      deep: true
    }
  },
  methods: {
    async init () {
      await this.setCurrentDateAndTime('entry')
      await this.setCurrentPage(1)
      await this.isMultiStep()
    },
    checkQuestion () {
      if (this.currentPage === 1) {
        this.$v.formAnswers.enterDateOfEntry.$touch()
        this.$v.formAnswers.enterTimeOfEntry.$touch()
        this.$v.formAnswers.temperature.$touch()
        if (
          !this.$v.formAnswers.enterDateOfEntry.$invalid &&
          !this.$v.formAnswers.enterTimeOfEntry.$invalid &&
          !this.$v.formAnswers.temperature.$invalid
        ) {
          const answers = this.answersById[this.questionTemperature.id]
          // this.nextPage()
          if (parseFloat(answers) > parseFloat(STOP_TEMP)) {
            this.setFailureDescription(this.selectedUser.itemName + ' has failed the temperature screening')
            this.failureStopDescription = 'Your temperature was above ' + STOP_TEMP + '° F'
            this.showFailedScreeningModal = !this.showFailedScreeningModal
          } else {
            this.nextPage()
          }
        }
      } else {
        this.$v.formAnswers[this.currentQuestion.section].$touch()
        if (!this.$v.formAnswers[this.currentQuestion.section].$invalid) {
          let continueToNextPage = true
          const answers = this.answersById[this.currentQuestionId]
          const values = this.currentQuestion.values

          if (this.currentQuestion.section === 'diagnosedWithCovid') {
            const yesValue = this.findValueByName('yes', values)
            answers.forEach((isChecked, key) => {
              if (isChecked && key === yesValue.id) {
                continueToNextPage = false
              }
            })

            if (!continueToNextPage) {
              this.setFailureDescription(this.selectedUser.itemName + ' has been diagnosed with COVID-19 or Influenza within the last 14 days')
              this.failureStopDescription = 'You have been diagnosed with COVID-19 or Influenza within the last 14 days'
              this.showFailedScreeningModal = !this.showFailedScreeningModal
            }
          }

          if (this.currentQuestion.section === 'experiencedAnyOfTheseSymptoms' || this.currentQuestion.section === 'experiencedAnyOfTheseAdditionalSymptoms') {
            const noValue = this.findValueByName('no', values)
            let numberChecked = 0
            answers.forEach((isChecked, key) => {
              if (isChecked) {
                numberChecked++
              }
              if (isChecked && key === noValue.id) {
                return true
              }
            })

            if (numberChecked >= 3) {
              continueToNextPage = false
            }

            if (!continueToNextPage) {
              this.setFailureDescription(this.selectedUser.itemName + ' has experienced more than 3 COVID-19 related symptoms in the last 72 hours')
              this.failureStopDescription = 'You are experiencing more than 3 COVID-19 related symptoms in the last 72 hours'
              this.showFailedScreeningModal = !this.showFailedScreeningModal
            }
          }

          if (this.currentQuestion.section === 'visitedStates') {
            const yesValue = this.findValueByName('yes', values)
            answers.forEach((isChecked, key) => {
              if (isChecked && key === yesValue.id) {
                continueToNextPage = false
              }
            })

            if (!continueToNextPage) {
              this.setFailureDescription(this.selectedUser.itemName + ' has visited a state listed on the travel ban within the last 14 days')
              this.failureStopDescription = 'You have visited a state listed on the travel ban within the last 14 days'
              this.showFailedScreeningModal = !this.showFailedScreeningModal
            }
          }

          if (this.canSubmitForm && continueToNextPage) {
            this.submitForm()
          }

          if (continueToNextPage) {
            this.nextPage()
          }
        }
      }
    },
    submitForm () {
      this.setFormSubmitted(!this.formSubmitted)
      this.addFormResults(this.answersById)
    },
    submitFormFailure () {
      this.setFailedScreening(true)
      this.showFailedScreeningModal = !this.showFailedScreeningModal
      this.submitForm()
    }
  }
}
</script>

<style scoped lang="scss">
.questionnaire-bottom {
  text-align: center;
}
</style>
